<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import Icon from "@/components/general/Icon/Icon.vue";
import LGLogoDefault from "@/assets/img/landg_logo.svg";
import TSBLogoDefault from "@/assets/img/tsb-logo.svg";
import { useDataLayerStore } from "@/stores";
import { EventTypeEnum } from "@/models";

interface Props {
    title?: string;
    phoneNumber?: string;
    lgLogo?: string;
    tsbLogo?: string;
    iconName?: string;
}

const {
    title = "Over 50s Fixed Life Insurance Plan",
    phoneNumber = "0800 072 4895",
    lgLogo = LGLogoDefault,
    tsbLogo = TSBLogoDefault,
    iconName = "phone",
} = defineProps<Props>();

const showPopover = ref(false);
const togglePopover = () => (showPopover.value = !showPopover.value);
const closePopover = () => (showPopover.value = false);

const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest("#contactBtn") && !target.closest("#contact-popover")) {
        closePopover();
    }
};

const dataLayer = useDataLayerStore();

onMounted(() => document.addEventListener("click", handleClickOutside));
onBeforeUnmount(() =>
    document.removeEventListener("click", handleClickOutside)
);
</script>

<template>
    <header class="app-header" role="banner">
        <div class="header-inner">
            <div class="header-logo" aria-label="Company logos">
                <img
                    :src="lgLogo"
                    alt="Legal and General logo"
                    class="logo-lg"
                />
                <img :src="tsbLogo" alt="TSB Bank logo" class="logo-tsb" />
            </div>
            <div class="header-right">
                <div class="header-contact">
                    <button
                        id="contactBtn"
                        class="contact-btn"
                        aria-label="Contact us by phone"
                        :aria-expanded="showPopover ? 'true' : 'false'"
                        @click.stop="
                            () => {
                                dataLayer.triggerEvent(
                                    EventTypeEnum.PageInteraction,
                                    `click|contactBtn`
                                );
                                togglePopover();
                            }
                        "
                    >
                        <div class="contact-content">
                            <Icon :name="iconName" />
                            <span class="contact-text">Contact</span>
                        </div>
                    </button>
                    <div
                        v-if="showPopover"
                        id="contact-popover"
                        class="contact-popover"
                        role="dialog"
                        aria-modal="false"
                        aria-labelledby="contactBtn"
                        @click.stop
                    >
                        <span class="popover-title">Call us on:</span><br />
                        <span class="popover-number">{{ phoneNumber }}</span
                        ><br />
                        <span class="popover-times">
                            8:30am - 8pm Monday to Friday<br />
                            9am - 1pm on Saturday </span
                        ><br />
                        <span class="popover-record"
                            >We may record and monitor calls</span
                        >
                    </div>
                </div>
                <h1 v-if="title" class="header-title">{{ title }}</h1>
            </div>
        </div>
    </header>
</template>

<style scoped>
.app-header {
    background-color: var(--pure-white);
    border-bottom: 4px solid var(--color-primary);
    position: relative;
    padding: 0 1rem;
}

.header-inner {
    padding: 0 15px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.header-logo {
    display: flex;
    align-items: center;
    height: 78px;
    margin: 1.1em 1.1em 1.1em 0;
}

.logo-lg {
    width: 78px;
    height: 78px;
}

.logo-tsb {
    width: 98px;
    height: 36px;
    margin-left: 8px;
    margin-top: 42px;
}

.header-right {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    height: 113.19px;
}

.header-title {
    font-size: 1.3rem;
    color: var(--color-primary);
    margin-bottom: 1.2rem;
    text-align: right;
    white-space: nowrap;
}

.header-contact {
    position: relative;
}

.contact-btn {
    display: flex;
    align-items: center;
    border: none;
    background: none;
    color: var(--grey-dark);
    cursor: pointer;
    padding: 0;
}

.contact-content {
    display: flex;
    align-items: center;
    padding: 10px 5px 10px 25px;
}

.contact-content svg {
    width: 20px;
    height: 20px;
}

.contact-text {
    font-size: 14.4px;
    margin-left: 4px;
}

.contact-btn:hover .contact-text,
.contact-btn:focus .contact-text {
    color: var(--primary-focus-outline);
}

.contact-popover {
    position: absolute;
    top: 100%;
    left: 40%;
    transform: translateX(-40%);
    margin-top: 0.5rem;
    background: var(--pure-white);
    border: 1px solid var(--grey-border);
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    z-index: 1080;
    min-width: 220px;
}

.contact-popover::before {
    content: "";
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 12px;
    height: 12px;
    background: var(--pure-white);
    border-left: 1px solid var(--grey-border);
    border-top: 1px solid var(--grey-border);
}

.popover-title {
    font-weight: bold;
    font-size: 0.95rem;
    color: var(--off-black);
    display: block;
    margin-bottom: 0.5rem;
}

.popover-number {
    display: block;
    margin: 0 0 0.5rem;
    font-size: 0.95rem;
    font-weight: bold;
    color: var(--off-black);
    text-decoration: none;
}

.popover-phone-number:hover {
    color: var(--signature-blue) !important;
}

.popover-times,
.popover-record {
    font-size: 0.875rem;
    color: var(--off-black);
}

.popover-title,
.popover-number,
.popover-record {
    display: inline-block;
    margin-right: 4px;
    white-space: nowrap;
}

@media screen and (width <= 584px) {
    .app-header {
        border-bottom: 2px solid var(--color-primary-light);
        padding: 0 15px;
    }

    .header-inner {
        padding: 0;
        flex-direction: row;
        justify-content: space-between;
    }

    .header-logo {
        height: 59px;
        margin: 8px 0;
    }

    .logo-lg {
        width: 59px;
        height: 59px;
    }

    .logo-tsb {
        width: 59px;
        height: 29px;
        margin-top: 30px;
    }

    .header-right {
        flex-direction: column-reverse;
        align-items: flex-end;
        justify-content: flex-start;
        height: fit-content;
    }

    .header-title {
        display: none;
    }

    .contact-content {
        width: 80px;
        justify-content: center;
        padding: 0;
    }

    .contact-text {
        display: none;
    }

    .contact-btn {
        height: 75px;
        width: 80px;
        border-left: 2px solid var(--color-primary-light);
    }

    .contact-popover {
        position: fixed;
        top: 75px;
        right: 8px;
        left: auto;
        width: auto;
        transform: none;
        border-radius: 8px;
        padding: 0.5rem 0.75rem;
    }

    .contact-popover::before {
        top: -6px;
        left: 80%;
        transform: translateX(-50%) rotate(45deg);
        width: 12px;
        height: 12px;
    }
}

@media screen and (width >= 993px) {
    .header-inner {
        max-width: 960px;
    }
}

@media screen and (width >= 1200px) {
    .header-inner {
        max-width: 1140px;
    }
}
</style>
